import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DeepLinkService } from './deep-link.service';

const CREATE_ACTIVITY_TOGGLE_ID = 'create-golf-event-toggle';

/** One-shot callback registered only during post-auth home landing (home page owns Shepherd). */
export type DeferredHomeTourHandler = () => void;

/**
 * Post-auth only: open create-activity modal on home, then run a deferred home-tour callback
 * after the popup closes. No-op for all other popup usage across the app.
 */
@Injectable({
  providedIn: 'root',
})
export class PostAuthActivityService {
  private readonly deepLink = inject(DeepLinkService);
  private readonly router = inject(Router);
  private pendingOpenOnHome = false;
  private deferredHomeTourArmed = false;
  private deferredHomeTourHandler: DeferredHomeTourHandler | null = null;
  /** When true, activity-popup close must not run the deferred home tour yet (Event Details is opening). */
  private holdDeferredHomeTourForEventPopup = false;

  navigateHomeAfterAuth(router: Router): void {
    if (this.deepLink.resolvePendingDeepLink()) {
      return;
    }
    this.pendingOpenOnHome = true;
    void router.navigate(['/home']);
  }

  hasPendingOpenOnHome(): boolean {
    return this.pendingOpenOnHome;
  }

  consumePendingOpenOnHome(): boolean {
    if (!this.pendingOpenOnHome) {
      return false;
    }
    this.pendingOpenOnHome = false;
    return true;
  }

  /**
   * Home page only: after post-auth popup, run `handler` once popup is closed on `/home`.
   */
  armDeferredHomeTour(handler: DeferredHomeTourHandler): void {
    this.deferredHomeTourArmed = true;
    this.deferredHomeTourHandler = handler;
  }

  cancelDeferredHomeTour(): void {
    this.deferredHomeTourArmed = false;
    this.deferredHomeTourHandler = null;
    this.holdDeferredHomeTourForEventPopup = false;
  }

  /**
   * Call before closing the activity modal when opening Event Details so the deferred
   * home tour does not appear on top of the create-event overlay.
   */
  deferHomeTourUntilEventPopupCloses(): void {
    if (!this.deferredHomeTourArmed) {
      return;
    }
    this.holdDeferredHomeTourForEventPopup = true;
  }

  /**
   * Safe to call on every activity-popup close app-wide; runs handler only when post-auth armed.
   */
  notifyActivityPopupClosed(): void {
    if (!this.deferredHomeTourArmed) {
      return;
    }
    if (!this.isHomePath(this.router.url)) {
      this.cancelDeferredHomeTour();
      return;
    }
    if (this.isActivityPopupOpen()) {
      return;
    }
    if (this.holdDeferredHomeTourForEventPopup || this.isEventPopupOpen()) {
      return;
    }
    const handler = this.deferredHomeTourHandler;
    this.cancelDeferredHomeTour();
    handler?.();
  }

  /**
   * Safe to call on every event-popup close; runs deferred home tour only when held from post-auth.
   */
  notifyEventPopupClosed(): void {
    if (!this.holdDeferredHomeTourForEventPopup) {
      return;
    }
    this.holdDeferredHomeTourForEventPopup = false;
    this.notifyActivityPopupClosed();
  }

  openActivityModal(): void {
    const toggle = document.getElementById(CREATE_ACTIVITY_TOGGLE_ID) as HTMLInputElement | null;
    if (!toggle) {
      return;
    }
    toggle.checked = true;
    toggle.dispatchEvent(new Event('change'));
  }

  tryOpenActivityModalWhenReady(onGiveUp?: () => void, maxAttempts = 25, intervalMs = 100): void {
    let attempts = 0;
    const tick = (): void => {
      if (document.getElementById(CREATE_ACTIVITY_TOGGLE_ID)) {
        this.openActivityModal();
        return;
      }
      if (++attempts < maxAttempts) {
        setTimeout(tick, intervalMs);
        return;
      }
      onGiveUp?.();
    };
    setTimeout(tick, 0);
  }

  private isActivityPopupOpen(): boolean {
    const toggle = document.getElementById(CREATE_ACTIVITY_TOGGLE_ID) as HTMLInputElement | null;
    return toggle?.checked === true;
  }

  private isEventPopupOpen(): boolean {
    const toggle = document.getElementById('create-user-event-toggle') as HTMLInputElement | null;
    return toggle?.checked === true;
  }

  private isHomePath(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return path === '/' || path === '' || path === '/home' || path.startsWith('/home/');
  }
}
